import angular from "angular";

import { CalculationListItem, Configs, Datasets, DatasetStatus, Dates, PeriodType, PeriodDateKind, WorkerListItem, PayrollListItem, DatasetType, ApiListItem, Avatar } from "@salaxy/core";

import { SessionService, UiHelpers } from "../../services";

/** Report template */
interface Template {
  /** Template id */
  id: string;
  /** Owner of the template */
  owner: string;
  /** Template label */
  label: string;
  /** Template configuration*/
  config: {
    columnState?: any,
    filterModel?: any,
    columnGroupState?: any,
    isPivotMode?: boolean,
  };
  /** Boolean indicating if the template is a user template */
  isReadOnly: boolean;
  /** Owners avatar */
  avatar?: Avatar;
}

/**
 * Controller base class for configurable grid reports.
 */
export abstract class ConfigurableGridReportControllerBase<TData> implements angular.IController {

  /** Usecase uri for report templates */
  public abstract usecaseUri: string;

  /** Current grid options. */
  public abstract gridOptions: any;

  /** ID for the element containing grid. */
  public abstract gridElmentId;

  /** default templates */
  protected abstract defaultTemplates: Template[];

  /** Current data */
  public data: TData;

  /** Current grid. */
  public grid: any;

  /** Options for data query. */
  public queryOptions:
    {
      period: {
        /** Period type for the query. */
        periodType: PeriodType,
        /** Ref date for the period. */
        refDate?: string,
        /** End date for the custom period. */
        endDate?: string,
        /** Period date kind */
        periodDateKind: PeriodDateKind,
        /** Selected workers */
        selectedWorkers: WorkerListItem[],
      },
      calculations: CalculationListItem[],
      payrolls: PayrollListItem[],
    } = {
      period: {
        periodType: PeriodType.Month,
        refDate: Dates.format(Dates.add("today",-1, "month"),"YYYY-MM-01"),
        endDate: null,
        periodDateKind: PeriodDateKind.SalaryDate,
        selectedWorkers: [],
      },
      calculations: [],
      payrolls: [],
  };

  /** Current report template */
  public templateId: string;

  /** All loaded templates */
  public templates: Template[] = [];

  constructor(protected datasets: Datasets, protected uiHelpers: UiHelpers, protected sessionService: SessionService) {
  }

  /**
   * Initialize controller values.
   */
  public $onInit() {
    const getSalaxyGridModule = (): Promise<any> => {
      // Support both esm and umd builds...
      return Promise.resolve(Configs.global.agGrid);
    };

    getSalaxyGridModule().then((agGridModule) => {
      const agGrid = agGridModule;
      const AG_GRID_LICENCE_KEY = "CompanyName=Suomen Palkanlaskenta Oy,LicensedGroup=Palkkaus.fi,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=3,LicensedProductionInstancesCount=1,AssetReference=AG-012462,ExpiryDate=17_December_2021_[v2]_MTYzOTY5OTIwMDAwMA==5d6e41ff68f667b432fc0424ad42ca1d";
      agGrid.LicenseManager.setLicenseKey(AG_GRID_LICENCE_KEY);
      if (!agGrid.ModuleRegistry.getRegisteredModules().length) {
        agGrid.ModuleRegistry.registerModules(agGrid.AllModules);
      }

      const eGridDiv = document.querySelector(this.gridElmentId);
      this.grid = new agGrid.Grid(eGridDiv, this.gridOptions);

      this.loadTemplates();
    });
  }

  /**
   * Returns the template definition
   * @param templateId Template id.
   */
  public getTemplate(templateId: string) {
    return this.templates.find((x) => x.id === templateId);
  }

  /**
   * Selects the template to show
   * @param templateId The selected template.
   */
  public selectTemplate(templateId: any) {
    this.templateId = templateId;
    if (this.grid) {
      this.loadTemplateConfig(templateId).then((template) => {
        this.setTemplateStateToGrid(template);
      });
    }
  }

  /**
   * Save template
   */
  public showSaveTemplate() {
    const currentTemplate = this.getTemplate(this.templateId);
    const item: {
      /** Boolean indicating a new template */
      isNew: boolean,
      /** Boolean indicating if the template can be edited. */
      isEditable: boolean,
      /** Label for the template */
      label: string,
    } = {
      isNew: currentTemplate.isReadOnly,
      isEditable: !currentTemplate.isReadOnly,
      label: currentTemplate.isReadOnly ? "Uusi raportti (" + (this.templates.filter((x) => !x.isReadOnly).length + 1) + ")" : currentTemplate.label,
    };

    const saveTemplate = (template: Template): Promise<Template> => {
      return this.datasets.save({
        id: template.id,
        info: {
          label: template.label,
          status: DatasetStatus.Template,
          type: DatasetType.ReportAdHoc,
        },
        usecase: {
          uri: this.usecaseUri
        },
        data: [{
          content: [
            template.config
          ]
        }]
      }).then((dataset) => ({
        id: dataset.id,
        owner: dataset.owner,
        label: dataset.info.label,
        config: dataset.data[0].content[0],
        isReadOnly: dataset.isReadOnly,
        avatar: this.sessionService.avatar,
      }));
    };

    this.uiHelpers.openEditDialog(
      "salaxy-components/report/modals/CalculationReportSaveTemplate.html",
      item,
      null).then((saveResult) => {
        if (saveResult.action === "ok") {
          const editedTemplate = saveResult.item;
          if (!editedTemplate.isNew) {
            currentTemplate.label = editedTemplate.label;
            this.setGridStateToTemplate(currentTemplate);
            saveTemplate(currentTemplate);
          } else {
            const newTemplate = {
              id: null,
              owner: null,
              label: editedTemplate.label,
              config: {},
              isReadOnly: false,
            };
            this.setGridStateToTemplate(newTemplate);
            saveTemplate(newTemplate).then((savedTemplate) => {
              this.templates.push(savedTemplate);
              this.selectTemplate(savedTemplate.id);
            });
          }
        } else if (saveResult.action === "delete") {
          this.showDeleteTemplate();
        }
      });
  }

  /**
   * Delete template
   */
  public showDeleteTemplate() {
    const template = this.getTemplate(this.templateId);
    this.uiHelpers.showConfirm(`Haluatko poistaa tämän raporttipohjan '${template.label}'?`).then((result) => {
      if (result) {
        if (!template.isReadOnly) {
          const idx = this.templates.findIndex((x) => x.id === this.templateId);
          this.templates.splice(idx, 1);
          this.selectTemplate(this.templates[idx - 1].id);
          this.datasets.delete(template.id);
        }
      }
    });
  }

  /**
   * Shows the period selection dialog and makes the search.
   */
  public showPeriodSelection() {
    this.uiHelpers.openEditDialog(
      "salaxy-components/report/modals/CalculationReportPeriodSelection.html",
      this.queryOptions.period,
      {
        showWorkerSelection: (workerPeriod: any) => {
          this.uiHelpers.openEditDialog(
            "salaxy-components/modals/calc/WorkerList.html",
            [].concat(...workerPeriod.selectedWorkers),
            {
              title: "Valitse työntekijät",
            }).then((selectionResult) => {
              if (selectionResult.action === "ok") {
                workerPeriod.selectedWorkers.splice(0, workerPeriod.selectedWorkers.length);
                workerPeriod.selectedWorkers.push(...selectionResult.item);
              }
            });
        }
      }).then((periodResult) => {
        if (periodResult.action === "ok") {
          this.queryOptions.period = periodResult.item;
          this.gridOptions.api.showLoadingOverlay();
          const workerIds = periodResult.item.selectedWorkers.length > 0 ? periodResult.item.selectedWorkers.map((x) => x.otherPartyInfo.avatar.id) : null;
          this.queryDataForPeriod(periodResult.item.refDate, periodResult.item.periodType, periodResult.item.endDate, periodResult.item.periodDateKind, workerIds)
            .then((dataResult) => {
              this.setData(dataResult);
            });
        }
      });
  }

  /**
   * Shows the calculation selection dialog and makes the search.
   */
  public showCalcsSelection() {
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/CalcList.html",
      this.queryOptions.calculations,
      {
        title: "Valitse maksetut palkat",
        category: "paid",
      }).then((selectionResult) => {
        if (selectionResult.action === "ok" && selectionResult.item.length > 0) {
          this.queryOptions.calculations = selectionResult.item;
          const ids = selectionResult.item.map((x) => x.id);
          this.gridOptions.api.showLoadingOverlay();
          this.queryDataForCalculationIds(ids).then((dataResult) => {
            this.setData(dataResult);
          });
        }
      });
  }

  /**
   * Shows the calculation selection dialog and makes the search.
   */
  public showPayrollSelection() {
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/payroll/PayrollList.html",
      this.queryOptions.payrolls,
      {
        title: "Valitse palkkalistat",
        category: "paid",
      }, "lg").then((selectionResult) => {
        if (selectionResult.action === "ok" && selectionResult.item.length > 0) {
          this.queryOptions.payrolls = selectionResult.item;
          const ids = selectionResult.item.reduce((all, x) => all.concat(x.businessObjects), []);
          this.gridOptions.api.showLoadingOverlay();
          this.queryDataForCalculationIds(ids).then((dataResult) => {
            this.setData(dataResult);
          });
        }
      });
  }

  /**
   * Gets the data based on given set of calculations.
   * @param calculationIds - Calculations that are the bases for the report.
   * @returns A Promise with result data.
   */
  public abstract queryDataForCalculationIds(calculationIds: string[]): Promise<TData>;

  /**
   * Gets the monthly / quarterly / yearly cumulative data for the current account.
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date, salary date or work date.
   * @param workers List of worker official ids.
   * @returns A Promise with result data.
   */
   public abstract queryDataForPeriod(refDate: string, periodType?: PeriodType | "all", endDate?: string, periodDateKind?: PeriodDateKind, workers?: string[]): Promise<TData>;

   /**
    * Processes and sets the data.
    */
  public abstract setData(result: TData);

  private loadTemplates() {
    this.templates.splice(0);
    const filter = `status eq '${DatasetStatus.Template}' and entityType eq '${DatasetType.ReportAdHoc}' and data/usecaseUri eq '${this.usecaseUri}'`;
    this.datasets.getOData({ $filter: filter }).then((result) => {
      const customerTemplates = result.items.map((x: ApiListItem) => ({
        id: x.id,
        owner: x.owner,
        label: x.shortText,
        config: null,
        isReadOnly: x.isReadOnly,
        avatar: x.ownerInfo.avatar,
      }));
      this.templates.push(...this.defaultTemplates.concat(...customerTemplates));
    });
  }

  private loadTemplateConfig(templateId: string) {
    const template = this.getTemplate(templateId);
    if (template.config) {
      return Promise.resolve(template);
    }
    else {
      return this.datasets.getSingle(template.id + (template.isReadOnly ? `@${template.owner}` : "")).then((dataset) => {
        template.config = dataset.data.length == 1 && dataset.data[0].content.length == 1 ? dataset.data[0].content[0] : {};
        return template;
      });
    }
  }

  private setTemplateStateToGrid(template: Template) {
    this.gridOptions.columnApi.setPivotMode(template.config.isPivotMode ?? false);
    if (template.config.columnState) {
      this.gridOptions.columnApi.applyColumnState({
        state: template.config.columnState,
        applyOrder: true
      });
    }
    else
    {
      this.gridOptions.columnApi.resetColumnState();
    }
    if (template.config.columnGroupState) {
      this.gridOptions.columnApi.setColumnGroupState(template.config.columnGroupState);
    }
    else
    {
      this.gridOptions.columnApi.resetColumnGroupState();
    }

    this.gridOptions.api.setFilterModel(template.config.filterModel);
  }

  private setGridStateToTemplate(template: Template) {
    template.config = template.config ?? {};
    template.config.columnState = this.gridOptions.columnApi.getColumnState();
    template.config.columnGroupState = this.gridOptions.columnApi.getColumnGroupState();
    template.config.filterModel = this.gridOptions.api.getFilterModel();
    template.config.isPivotMode = this.gridOptions.columnApi.isPivotMode();
  }
}
